/**
 * Insights CSV export utilities
 * Converts staff/team performance snapshots to CSV and triggers a download
 */

import {
  parseDuration,
  formatDurationMinutes,
  formatDateTimeForTooltip,
  buildAssigneeChartData,
  buildActivityOverTimeSeries,
  buildUserActivityOverTimeSeries,
  getReadyForReviewSeries,
  buildReadyForReviewRows,
  buildConsultationsReadyRows,
  buildDocumentProcessingPoints,
  buildConsultationProcessingPoints,
} from './chartSeries.js';

const escapeCSVField = (field) => {
  if (field == null) return '';
  const stringField = String(field);
  if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }
  return stringField;
};

const convertToCSV = (data, columnConfig) => {
  const headers = columnConfig.map(col => escapeCSVField(col.header));
  const csvRows = [headers.join(',')];

  data.forEach(item => {
    const row = columnConfig.map(col => {
      const value = col.formatter ? col.formatter(item) : item[col.key];
      return escapeCSVField(value);
    });
    csvRows.push(row.join(','));
  });

  return csvRows.join('\n');
};

const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatAverageReviewTime = (totalTimeMinutes, totalDocs) => {
  const avgMinutes = totalDocs > 0 ? totalTimeMinutes / totalDocs : 0;
  return formatDurationMinutes(avgMinutes);
};

const staffDocumentsColumns = (userIdToName) => [
  {
    key: 'name',
    header: 'Name',
    formatter: (row) => userIdToName[row.userId] || row.userId
  },
  {
    key: 'totalWorked',
    header: 'Documents worked',
    formatter: (row) => row.totalWorked || 0
  },
  {
    key: 'totalDocs',
    header: 'Documents filed away',
    formatter: (row) => row.totalFiledAway
  },
  {
    key: 'totalTime',
    header: 'Time reviewing documents',
    formatter: (row) => formatDurationMinutes(parseDuration(row.totalTimeInReview))
  },
  {
    key: 'avgTime',
    header: 'Average time reviewing each document',
    formatter: (row) => formatAverageReviewTime(parseDuration(row.totalTimeInReview), row.totalFiledAway)
  },
  {
    key: 'totalAssigned',
    header: 'Documents assigned',
    formatter: (row) => row.totalAssigned || 0
  },
  {
    key: 'assigningTime',
    header: 'Time assigning documents',
    formatter: (row) => formatDurationMinutes(parseDuration(row.totalTimeAssigning))
  },
  {
    key: 'avgAssigningTime',
    header: 'Average time assigning each document',
    formatter: (row) => formatAverageReviewTime(parseDuration(row.totalTimeAssigning), row.totalAssigned || 0)
  },
  {
    key: 'timeSpentFiling',
    header: 'Time spent filing',
    formatter: (row) => formatDurationMinutes(parseDuration(row.totalTimeInReview))
  },
  {
    key: 'timeSpentAssigning',
    header: 'Time spent assigning',
    formatter: (row) => formatDurationMinutes(parseDuration(row.totalTimeAssigning))
  },
  {
    key: 'totalWorkTime',
    header: 'Total time filing and assigning',
    formatter: (row) => formatDurationMinutes(
      parseDuration(row.totalTimeInReview) + parseDuration(row.totalTimeAssigning)
    )
  }
];

const teamDocumentsColumns = [
  {
    key: 'teamName',
    header: 'Team Name',
    formatter: (row) => row.teamName
  },
  {
    key: 'memberCount',
    header: 'Members',
    formatter: (row) => row.memberCount
  },
  {
    key: 'totalWorked',
    header: 'Documents worked',
    formatter: (row) => row.totalWorked || 0
  },
  {
    key: 'totalDocs',
    header: 'Documents filed away',
    formatter: (row) => row.totalFiledAway
  },
  {
    key: 'totalTime',
    header: 'Time reviewing documents',
    formatter: (row) => formatDurationMinutes(row.totalTimeMinutes)
  },
  {
    key: 'avgTime',
    header: 'Average time reviewing each document',
    formatter: (row) => formatDurationMinutes(row.avgTimePerDoc)
  },
  {
    key: 'totalAssigned',
    header: 'Documents assigned',
    formatter: (row) => row.totalAssigned || 0
  },
  {
    key: 'assigningTime',
    header: 'Time assigning documents',
    formatter: (row) => formatDurationMinutes(row.assigningTimeMinutes)
  },
  {
    key: 'avgAssigningTime',
    header: 'Average time assigning each document',
    formatter: (row) => formatDurationMinutes(row.avgAssigningTime)
  },
  {
    key: 'timeSpentFiling',
    header: 'Time spent filing',
    formatter: (row) => formatDurationMinutes(row.totalTimeMinutes)
  },
  {
    key: 'timeSpentAssigning',
    header: 'Time spent assigning',
    formatter: (row) => formatDurationMinutes(row.assigningTimeMinutes)
  },
  {
    key: 'totalWorkTime',
    header: 'Total time filing and assigning',
    formatter: (row) => formatDurationMinutes(row.totalWorkMinutes)
  }
];

const staffTasksColumns = (userIdToName) => [
  {
    key: 'name',
    header: 'Name',
    formatter: (row) => userIdToName[row.userId] || row.userId
  },
  {
    key: 'totalCompleted',
    header: 'Tasks completed',
    formatter: (row) => row.totalCompleted
  }
];

const teamTasksColumns = [
  {
    key: 'teamName',
    header: 'Team Name',
    formatter: (row) => row.teamName
  },
  {
    key: 'memberCount',
    header: 'Members',
    formatter: (row) => row.memberCount
  },
  {
    key: 'totalCompleted',
    header: 'Tasks completed',
    formatter: (row) => row.totalCompleted
  }
];

const buildFilename = (prefix) => {
  const timestamp = new Date().toISOString();
  return `${prefix}_${timestamp}.csv`;
};

const downloadRows = (data, columnConfig, filenamePrefix) => {
  if (!data || data.length === 0) return;
  const csvContent = convertToCSV(data, columnConfig);
  downloadCSV(csvContent, buildFilename(filenamePrefix));
};

export const downloadStaffDocumentsCSV = (staffData, userIdToName) => {
  downloadRows(staffData, staffDocumentsColumns(userIdToName), 'staff_performance_documents');
};

export const downloadTeamDocumentsCSV = (teamData) => {
  downloadRows(teamData, teamDocumentsColumns, 'team_performance_documents');
};

export const downloadStaffTasksCSV = (staffData, userIdToName) => {
  downloadRows(staffData, staffTasksColumns(userIdToName), 'staff_performance_tasks');
};

export const downloadTeamTasksCSV = (teamData) => {
  downloadRows(teamData, teamTasksColumns, 'team_performance_tasks');
};

const formatAverageEncounterTime = (totalTimeMinutes, totalEncounters) => {
  const avgMinutes = totalEncounters > 0 ? totalTimeMinutes / totalEncounters : 0;
  return formatDurationMinutes(avgMinutes);
};

const staffConsultationsColumns = (userIdToName) => [
  {
    key: 'name',
    header: 'Name',
    formatter: (row) => userIdToName[row.userId] || row.userId
  },
  {
    key: 'totalEncounters',
    header: 'Encounters',
    formatter: (row) => row.totalEncounters
  },
  {
    key: 'totalTime',
    header: 'Time in encounters',
    formatter: (row) => formatDurationMinutes(parseDuration(row.totalTimeInEncounters))
  },
  {
    key: 'avgTime',
    header: 'Average recording duration',
    formatter: (row) => formatAverageEncounterTime(parseDuration(row.totalTimeInEncounters), row.totalEncounters)
  }
];

const teamConsultationsColumns = [
  {
    key: 'teamName',
    header: 'Team Name',
    formatter: (row) => row.teamName
  },
  {
    key: 'memberCount',
    header: 'Members',
    formatter: (row) => row.memberCount
  },
  {
    key: 'totalEncounters',
    header: 'Encounters',
    formatter: (row) => row.totalEncounters
  },
  {
    key: 'totalTime',
    header: 'Time in encounters',
    formatter: (row) => formatDurationMinutes(row.totalTimeMinutes)
  },
  {
    key: 'avgTime',
    header: 'Average recording duration',
    formatter: (row) => formatDurationMinutes(row.avgTimePerEncounter)
  }
];

export const downloadStaffConsultationsCSV = (staffData, userIdToName) => {
  downloadRows(staffData, staffConsultationsColumns(userIdToName), 'staff_performance_scribe');
};

export const downloadTeamConsultationsCSV = (teamData) => {
  downloadRows(teamData, teamConsultationsColumns, 'team_performance_scribe');
};

const slugify = (value) => {
  const slug = String(value || 'chart')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return slug || 'chart';
};

const aggregationSuffix = (mode) => (mode === 'individual' ? 'individual' : 'cumulative');

const uniqueNames = (names) => {
  const seen = new Set();
  const unique = [];
  names.forEach(name => {
    if (!seen.has(name)) {
      seen.add(name);
      unique.push(name);
    }
  });
  return unique;
};

const downloadTimestampSeries = (rows, seriesColumns, filenamePrefix) => {
  downloadRows(rows, [
    {
      key: 'timestamp',
      header: 'Time',
      formatter: (row) => formatDateTimeForTooltip(row.timestamp),
    },
    ...seriesColumns,
  ], filenamePrefix);
};

export const downloadReadyForReviewCSV = (data, type, filenamePrefix) => {
  const series = getReadyForReviewSeries(data, type);
  downloadTimestampSeries(
    buildReadyForReviewRows(data, type),
    series.map(item => ({
      key: item.name,
      header: item.name,
      formatter: (row) => row[item.name] ?? 0,
    })),
    filenamePrefix
  );
};

export const downloadConsultationsReadyCSV = (data) => {
  downloadTimestampSeries(
    buildConsultationsReadyRows(data),
    [{ key: 'readyForReview', header: 'Ready for Review' }],
    'consultations_ready_for_review'
  );
};

export const downloadActivityOverTimeCSV = ({
  data,
  actions,
  userActionLabels,
  filteredUserIds,
  mode,
  filenamePrefix,
}) => {
  const rows = buildActivityOverTimeSeries(data, actions, filteredUserIds, mode);
  downloadTimestampSeries(
    rows,
    (actions || []).map(action => ({
      key: action,
      header: userActionLabels[action] || action,
      formatter: (row) => row[action] ?? 0,
    })),
    `${filenamePrefix}_${aggregationSuffix(mode)}`
  );
};

export const downloadUserActivityOverTimeCSV = ({
  data,
  selectedAction,
  userIdToName,
  filteredUserIds,
  mode,
  filenamePrefix,
}) => {
  const { userNames, rows } = buildUserActivityOverTimeSeries({
    data,
    selectedAction,
    userIdToName,
    filteredUserIds,
    mode,
  });
  downloadTimestampSeries(
    rows,
    uniqueNames(userNames).map(name => ({
      key: name,
      header: name,
      formatter: (row) => row[name] ?? 0,
    })),
    `${filenamePrefix}_${aggregationSuffix(mode)}`
  );
};

export const downloadAssigneeChartCSV = (documentsAssigned, userIdToName, teams) => {
  downloadRows(
    buildAssigneeChartData(documentsAssigned, userIdToName, teams),
    [
      { key: 'assignee', header: 'Assignee' },
      { key: 'isTeam', header: 'Type', formatter: (row) => (row.isTeam ? 'Team' : 'User') },
      { key: 'count', header: 'Documents assigned' },
    ],
    'assigned_documents_by_assignee'
  );
};

const downloadDurationPoints = (points, timeHeader, durationHeader, filenamePrefix) => {
  downloadRows(
    points,
    [
      {
        key: 'timestamp',
        header: timeHeader || 'Time',
        formatter: (row) => formatDateTimeForTooltip(new Date(row.timestamp).toISOString()),
      },
      {
        key: 'duration',
        header: durationHeader || 'Duration',
        formatter: (row) => formatDurationMinutes(row.duration),
      },
    ],
    filenamePrefix
  );
};

export const downloadDocumentProcessingCSV = (reviewData, selectedMetric, metricLabel, timeHeader) => {
  downloadDurationPoints(
    buildDocumentProcessingPoints(reviewData, selectedMetric),
    timeHeader,
    metricLabel,
    `document_processing_times_${slugify(metricLabel)}`
  );
};

export const downloadConsultationProcessingCSV = (reviewData, selectedMetric, metricLabel, timeHeader) => {
  downloadDurationPoints(
    buildConsultationProcessingPoints(reviewData, selectedMetric),
    timeHeader,
    metricLabel,
    `consultation_times_${slugify(metricLabel)}`
  );
};
